import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { auth } from "../../services/auth";
import { usePublicBrand } from "../../context/BrandContext";
import {
  FField,
  StrBar,
  ErrBox,
  BtnPrimary,
  Spinner,
  pwStrength,
  generatePassword,
} from "./AuthComponents";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/* ── Logo zone — logo only, no name text (matches Login/Register) ── */
function LogoZone({ publicBrand }) {
  const [imgErr, setImgErr] = useState(false);
  const src = publicBrand?.logoUrl || "/assets/project_logo.png";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: 28,
        paddingBottom: 24,
        borderBottom: "1px solid #edf0f4",
      }}
    >
      {imgErr ? (
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            background: "#f0f2f5",
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "1rem",
              color: "#111111",
              letterSpacing: "0.04em",
            }}
          >
            MDP
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt="Logo"
          style={{ height: 64, maxWidth: 180, objectFit: "contain" }}
          onError={() => setImgErr(true)}
        />
      )}
    </div>
  );
}

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const q = useQuery();
  const email = q.get("email") || "";
  const token = q.get("token") || "";
  const publicBrand = usePublicBrand();

  const [validated, setValidated] = useState(false);
  const [validating, setValidating] = useState(true);
  const [err, setErr] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const { score, label } = pwStrength(password);
  const match = password && confirm && password === confirm;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await auth.validateResetToken(email, token);
        if (mounted) setValidated(true);
      } catch (e) {
        if (mounted) setErr(e?.data?.message || "Invalid or expired link.");
      } finally {
        if (mounted) setValidating(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [email, token]);

  async function handleSubmit(e) {
    e?.preventDefault();
    setErr(null);
    if (score < 3) return setErr("Please choose a stronger password.");
    if (!match) return setErr("Passwords do not match.");
    setSubmitting(true);
    try {
      await auth.performPasswordReset(email, token, password);
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1600);
    } catch (e) {
      setErr(e?.data?.message || "Could not reset password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f6f8",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: "#ffffff",
          border: "1px solid #e0e5ee",
          borderRadius: 18,
          padding: "40px 40px",
          boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
        }}
      >
        {/* Logo — only the image, no name text */}
        <LogoZone publicBrand={publicBrand} />

        {/* Validating */}
        {validating && (
          <div
            style={{
              textAlign: "center",
              padding: "20px 0",
              color: "#6b7280",
              fontFamily: "'DM Sans'",
            }}
          >
            <Spinner /> &nbsp;Validating reset link…
          </div>
        )}

        {/* Invalid link */}
        {!validating && err && !done && (
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.6rem",
                letterSpacing: "0.04em",
                color: "#9ca3af",
                marginBottom: 6,
              }}
            >
              Link invalid
            </div>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "2.2rem",
                lineHeight: 1.1,
                color: "#111827",
                margin: "0 0 12px",
              }}
            >
              Expired Link
            </h2>
            <ErrBox msg={err} />
            <Link
              to="/forgot-password"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                background: "#111111",
                color: "#ffffff",
                borderRadius: 10,
                padding: "13px 22px",
                fontFamily: "'DM Sans'",
                fontWeight: 600,
                fontSize: "0.9rem",
                textDecoration: "none",
              }}
            >
              Request a New Link <ArrowRight size={15} />
            </Link>
          </div>
        )}

        {/* Reset form */}
        {!validating && validated && !done && (
          <>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.12)",
                  marginBottom: 16,
                }}
              >
                <Lock size={24} style={{ color: "#111111" }} />
              </div>
              <div
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.6rem",
                  letterSpacing: "0.04em",
                  color: "#9ca3af",
                  marginBottom: 6,
                }}
              >
                Set new password
              </div>
              <h2
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "2.2rem",
                  lineHeight: 1.1,
                  color: "#111827",
                  margin: "0 0 8px",
                }}
              >
                Reset Your Password
              </h2>
              <p
                style={{
                  fontFamily: "'DM Sans'",
                  fontSize: "0.875rem",
                  color: "#6b7280",
                  margin: 0,
                  lineHeight: 1.6,
                  fontWeight: 300,
                }}
              >
                Choose a strong new password for your account.
              </p>
            </div>

            <ErrBox msg={err} />

            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
            >
              <FField
                label="New password"
                icon={Lock}
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="••••••••"
                required
              />
              <FField
                label="Confirm password"
                icon={Lock}
                type="password"
                value={confirm}
                onChange={setConfirm}
                status={confirm ? (match ? "ok" : "er") : ""}
                required
              />

              {password && (
                <div>
                  <StrBar score={score} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 4,
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: "0.66rem",
                      color: "#9ca3af",
                    }}
                  >
                    <span>Strength</span>
                    <span
                      style={{
                        color:
                          score >= 4
                            ? "#16a34a"
                            : score >= 3
                              ? "#555555"
                              : "#dc2626",
                      }}
                    >
                      {label}
                    </span>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  const p = generatePassword();
                  setPassword(p);
                  setConfirm(p);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  fontFamily: "'DM Sans'",
                  fontSize: "0.78rem",
                  color: "#111111",
                }}
              >
                <Sparkles size={12} /> Generate strong password
              </button>

              <BtnPrimary
                type="submit"
                disabled={submitting || score < 3 || !match}
                style={{ marginTop: 4 }}
              >
                {submitting ? (
                  <>
                    <Spinner /> Updating…
                  </>
                ) : (
                  <>
                    Update Password <ArrowRight size={15} />
                  </>
                )}
              </BtnPrimary>
            </form>

            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Link
                to="/login"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontFamily: "'DM Sans'",
                  fontSize: "0.8rem",
                  color: "#9ca3af",
                  textDecoration: "none",
                }}
              >
                <ArrowLeft size={13} /> Back to Sign In
              </Link>
            </div>
          </>
        )}

        {/* Success */}
        {done && (
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.2)",
                marginBottom: 16,
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#16a34a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.6rem",
                letterSpacing: "0.04em",
                color: "#9ca3af",
                marginBottom: 6,
              }}
            >
              Done
            </div>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "2.2rem",
                lineHeight: 1.1,
                color: "#111827",
                margin: "0 0 10px",
              }}
            >
              Password Updated!
            </h2>
            <p
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.875rem",
                color: "#6b7280",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Redirecting to sign in…
            </p>
          </div>
        )}
      </div>

      {/* T&C + Privacy Policy — below the card */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          marginTop: 20,
          flexWrap: "wrap",
        }}
      >
        <Link
          to={`${process.env.REACT_APP_LANDING_URL}/terms`}
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.75rem",
            color: "#9ca3af",
            textDecoration: "none",
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
        >
          Terms &amp; Conditions
        </Link>
        <span style={{ color: "#d1d5db", fontSize: "0.75rem" }}>·</span>
        <Link
          to={`${process.env.REACT_APP_LANDING_URL}/privacy`}
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.75rem",
            color: "#9ca3af",
            textDecoration: "none",
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
        >
          Privacy Policy
        </Link>
      </div>
    </div>
  );
}
